import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { useLocation, useNavigation } from 'react-router-dom';
import { NavigationLoaderContext } from './navigationLoader.context';

function NavigationLoaderOverlay() {
  return (
    <div
      className="fixed inset-0 z-[1200] flex cursor-wait items-center justify-center bg-white/80 backdrop-blur-[1px]"
      aria-busy="true"
      aria-live="polite"
      role="status"
    >
      <Loader2 className="h-10 w-10 animate-spin text-[#4A7B9D]" aria-hidden />
      <span className="sr-only">Loading page</span>
    </div>
  );
}

export function NavigationLoaderProvider({ children }: { children: ReactNode }) {
  const [pendingSinceKey, setPendingSinceKey] = useState<string | null>(null);
  const routerLoadingSeenRef = useRef(false);
  const navigation = useNavigation();
  const location = useLocation();

  const isRouterLoading = navigation.state === 'loading';
  const isPendingIdle =
    pendingSinceKey !== null &&
    pendingSinceKey === location.key &&
    navigation.state === 'idle';
  const isActive = isRouterLoading || isPendingIdle;

  useEffect(() => {
    if (navigation.state === 'loading') {
      routerLoadingSeenRef.current = true;
    }
  }, [navigation.state]);

  // Clear stale pending state once SPA navigation finishes.
  useEffect(() => {
    if (navigation.state !== 'idle' || pendingSinceKey === null) return;

    const clearPending = () => {
      setPendingSinceKey(null);
      routerLoadingSeenRef.current = false;
    };

    if (pendingSinceKey !== location.key) {
      queueMicrotask(clearPending);
      return;
    }

    // Returned to the same history entry (e.g. browser back) after a route transition.
    if (routerLoadingSeenRef.current) {
      queueMicrotask(clearPending);
    }
  }, [navigation.state, pendingSinceKey, location.key]);

  const startNavigation = useCallback(() => {
    routerLoadingSeenRef.current = false;
    setPendingSinceKey(location.key);
  }, [location.key]);

  const stopNavigation = useCallback(() => {
    setPendingSinceKey(null);
  }, []);

  const navigateTo = useCallback(
    (url: string) => {
      setPendingSinceKey(location.key);
      window.location.href = url;
    },
    [location.key]
  );

  const value = useMemo(
    () => ({
      isActive,
      startNavigation,
      stopNavigation,
      navigateTo,
    }),
    [isActive, navigateTo, startNavigation, stopNavigation]
  );

  return (
    <NavigationLoaderContext.Provider value={value}>
      {children}
      {isActive ? <NavigationLoaderOverlay /> : null}
    </NavigationLoaderContext.Provider>
  );
}
